// The root, the signed in app and the small parts every area's routes use.
import { AUTOMATIONS } from "@steward/shared";
import { createRootRoute, createRoute, Navigate, Outlet, useParams } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PreviewBar } from "../../demo/PreviewBar";
import { takeAfterSignIn } from "../../screens/ConnectClaude";
import { MySchedule } from "../../screens/CrewRoute";
import { MyDay } from "../../screens/Other";
import { AutomationPage, MESSAGE_SECTIONS, type MessageSection } from "../../screens/settings/Automations";
import { FeatureSettings } from "../../screens/settings/Feature";
import { useSession } from "../session";
import { UpdateWatch } from "../UpdateWatch";
import { SignedIn } from "./signed-in";

export const rootRoute = createRootRoute({
  component: () => (
    <>
      <Outlet />
      {__PREVIEW__ ? null : <UpdateWatch />}
      {__PREVIEW__ ? <PreviewBar /> : null}
    </>
  ),
});

export const app = createRoute({ getParentRoute: () => rootRoute, id: "app", component: SignedIn });

export function Start() {
  const { role } = useSession();
  // Signed in from Connect Claude: back to its page to press Allow.
  const after = takeAfterSignIn();
  if (after) {
    window.location.replace(after);
    return null;
  }
  return <Navigate to={role === "crew" ? "/my-day" : "/home"} />;
}

/** Only for roles that may see it; others land on their start page. */
export function Allowed({
  action,
  children,
}: {
  action: Parameters<ReturnType<typeof useSession>["can"]>[0];
  children: ReactNode;
}) {
  const { can } = useSession();
  return can(action) ? <>{children}</> : <Start />;
}

/** My day (the crew's Home) and My schedule are the crew's pages; the office goes to Home and Schedule. */
export function CrewOnly({ page }: { page?: "schedule" }) {
  const { role } = useSession();
  if (role !== "crew") return <Navigate to={page ? "/schedule" : "/home"} />;
  return page ? <MySchedule /> : <MyDay />;
}

/** The agency (The Marketers) gets its own screen in place of the home service one (draft 13). */
export function ByTrade({ agency, other }: { agency: ReactNode; other: ReactNode }) {
  const { company } = useSession();
  return <>{company.trade === "agency" ? agency : other}</>;
}

/** An agency only page; anywhere else it goes Home. */
export function AgencyOnly({ children }: { children: ReactNode }) {
  const { company } = useSession();
  return company.trade === "agency" ? <>{children}</> : <Navigate to="/home" />;
}

export const page = <P extends string>(path: P, component: () => ReactNode) =>
  createRoute({ getParentRoute: () => app, path, component });

/** A settings page that moved to its feature: old links and bookmarks land there, at the same card. */
export function Moved({ to }: { to: string }) {
  return <Navigate to={to} hash replace />;
}

/** ?new=true opens the New invoice or New request box, so the side menu's Create can open it from any page. */
export const newSearch = (s: Record<string, unknown>): { new?: boolean } =>
  s.new === true || s.new === "true" ? { new: true } : {};

/** A section's reminder page (Quote settings, then Quote follow-up), the same page as in Settings but inside the section. */
export const sectionMessagePage = <P extends string>(path: P, section: MessageSection) => {
  const r = createRoute({
    getParentRoute: () => app,
    path,
    component: function SectionMessageRoute(): ReactNode {
      const { kind } = useParams({ strict: false });
      const s = MESSAGE_SECTIONS[section];
      const k = s.kinds.find((x) => x === kind);
      return k ? (
        <Allowed action="settings.read">
          <FeatureSettings title={AUTOMATIONS[k].title} back={s.to} backLabel={s.label}>
            <AutomationPage kind={k} back={null} />
          </FeatureSettings>
        </Allowed>
      ) : (
        <Navigate to={s.to} />
      );
    },
  });
  return r;
};
